import { type FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { EvmAddress, Hex } from "@coinbase/cdp-core";
import { useOrder } from "../hooks/useOrders";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { useEscrowAction } from "../lib/coinbase/useEscrowAction";
import { cdpNetworkForChainId } from "../lib/coinbase/network";
import { apiClient, newIdempotencyKey } from "../lib/api-client";
import { uploadToPresignedTarget } from "../lib/upload";
import { rememberDisputeForOrder } from "../lib/disputeLinks";
import { ErrorBanner, LoadingState, describeError } from "../components/States";
import { truncateFilename } from "../lib/format";

const CATEGORIES = [
  "Item not as described",
  "Never delivered / not downloadable",
  "Corrupted or unusable file",
  "Other",
];

interface EvidenceItem {
  file: File;
  status: "pending" | "uploading" | "done" | "error";
  uploadId?: string;
  error?: string;
}

export function DisputeSubmitPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { data: order, isLoading, isError, error } = useOrder(orderId);
  const { data: config } = usePublicConfig();
  const { smartAccountAddress } = useAuthPrincipal();
  const escrowAction = useEscrowAction();

  const [category, setCategory] = useState(CATEGORIES[0] ?? "");
  const [claim, setClaim] = useState("");
  const [evidence, setEvidence] = useState<EvidenceItem[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!orderId) return null;
  if (isLoading) return <LoadingState label="Loading order…" />;
  if (isError) return <ErrorBanner error={error} />;
  if (!order) return null;

  // Hoisted function declarations below don't retain narrowing from the guards above, so
  // capture a definitely-typed binding once here.
  const safeOrderId: string = orderId;

  async function handleFiles(files: FileList) {
    const selected = Array.from(files).slice(0, 10 - evidence.length);
    for (const file of selected) {
      const index = evidence.length;
      setEvidence((prev) => [...prev, { file, status: "uploading" }]);
      try {
        const idempotencyKey = newIdempotencyKey();
        const presigned = await apiClient.presignDisputeEvidence(
          safeOrderId,
          {
            filename: file.name,
            declaredMimeType: file.type || "application/octet-stream",
            sizeBytes: file.size,
          },
          idempotencyKey,
        );
        const result = await uploadToPresignedTarget(presigned, file, () => {});
        const completed = await apiClient.completeDisputeEvidence(
          safeOrderId,
          presigned.uploadId,
          result.etag,
          idempotencyKey,
        );
        setEvidence((prev) =>
          prev.map((item, i) =>
            i === index ? { ...item, status: "done", uploadId: completed.id } : item,
          ),
        );
      } catch (err) {
        setEvidence((prev) =>
          prev.map((item, i) =>
            i === index ? { ...item, status: "error", error: describeError(err) } : item,
          ),
        );
      }
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (claim.trim().length < 1) {
      setSubmitError("Describe the problem before submitting.");
      return;
    }
    const evidenceUploadIds = evidence
      .filter((item) => item.status === "done" && item.uploadId)
      .map((item) => item.uploadId as string);

    setIsSubmitting(true);
    try {
      const dispute = await apiClient.createDispute(
        safeOrderId,
        { category, claim: claim.trim(), evidenceUploadIds },
        newIdempotencyKey(),
      );
      rememberDisputeForOrder(safeOrderId, dispute.id);

      const cdpNetwork = config ? cdpNetworkForChainId(config.chainId) : null;
      // openDisputeCall's evidenceHash is computed server-side and committed to the Dispute row
      // (see apps/api/src/services/disputes.ts) — the chain worker validates the onchain
      // DisputeOpened event's hash against that same value, so it must never be re-derived here.
      if (smartAccountAddress && cdpNetwork && dispute.openDisputeCall) {
        await escrowAction.submitRaw({
          smartAccountAddress,
          network: cdpNetwork,
          to: dispute.openDisputeCall.to as EvmAddress,
          data: dispute.openDisputeCall.data as Hex,
          value: BigInt(dispute.openDisputeCall.value),
        });
      }
      navigate(`/disputes/${dispute.id}`, { replace: true });
    } catch (err) {
      setSubmitError(describeError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <h1>Open a dispute</h1>
      <p className="muted">
        Opening a dispute pauses release of this order&apos;s held principal until an authorized
        arbitrator resolves it via the multisig. The platform fee already collected remains
        nonrefundable regardless of the outcome.
      </p>

      <form className="card stack" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="category">Category</label>
          <select id="category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="claim">What happened?</label>
          <textarea
            id="claim"
            required
            maxLength={5_000}
            value={claim}
            onChange={(e) => setClaim(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="evidence">Evidence (optional, up to 10 files)</label>
          <input
            id="evidence"
            type="file"
            multiple
            disabled={evidence.length >= 10}
            onChange={(e) => {
              if (e.target.files) void handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {evidence.length > 0 && (
          <ul className="stack text-sm">
            {evidence.map((item, i) => (
              <li key={i}>
                {truncateFilename(item.file.name)} — {item.status === "uploading" && "uploading…"}
                {item.status === "done" && "uploaded"}
                {item.status === "error" && `failed: ${item.error}`}
              </li>
            ))}
          </ul>
        )}

        {submitError && (
          <p className="field-error-text" role="alert">
            {submitError}
          </p>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting || escrowAction.phase === "awaiting-signature"}
        >
          {isSubmitting || escrowAction.phase === "awaiting-signature"
            ? "Submitting…"
            : "Submit dispute"}
        </button>
      </form>
    </div>
  );
}
